/**
 * Converts SRT subtitle format to WebVTT format for HTML5 video
 */
export function convertSRTtoWebVTT(srtContent: string): string {
  // WebVTT header
  let webvtt = 'WEBVTT\n\n';
  
  // Split by double newlines to get subtitle blocks
  const blocks = srtContent.trim().split(/\n\s*\n/);
  
  for (const block of blocks) {
    const lines = block.trim().split('\n');
    
    if (lines.length < 2) continue;
    
    // Skip the sequence number (first line)
    // Get timestamp (second line)
    const timestampLine = lines[1];
    
    // Convert SRT timestamp format (00:00:00,000 --> 00:00:00,000)
    // to WebVTT format (00:00:00.000 --> 00:00:00.000)
    const webvttTimestamp = timestampLine.replace(/,/g, '.');
    
    // Get text (remaining lines)
    const text = lines.slice(2).join('\n');
    
    // Add to WebVTT
    webvtt += `${webvttTimestamp}\n${text}\n\n`;
  }
  
  return webvtt;
}

/**
 * Fetch SRT subtitle and convert to WebVTT blob URL
 * Uses backend proxy to avoid CORS issues
 */
export async function fetchAndConvertSubtitle(srtUrl: string, proxyBaseUrl?: string): Promise<string> {
  try {
    // Use backend proxy if base URL is provided
    const fetchUrl = proxyBaseUrl 
      ? `${proxyBaseUrl}/api/vidsrc/subtitle-file?url=${encodeURIComponent(srtUrl)}`
      : srtUrl;
    
    const response = await fetch(fetchUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch subtitle: ${response.statusText}`);
    }
    
    const srtContent = await response.text();
    const webvttContent = convertSRTtoWebVTT(srtContent);
    
    // Create blob and blob URL
    const blob = new Blob([webvttContent], { type: 'text/vtt' });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching/converting subtitle:', error);
    throw error;
  }
}

export interface SubtitleTrack {
  label: string;
  file: string;
  blobUrl?: string; // WebVTT blob URL after conversion
}

/**
 * Get ISO 639-1 language code from subtitle label
 */
export function getLanguageCode(label: string): string {
  const langMap: { [key: string]: string } = {
    'english': 'en',
    'spanish': 'es',
    'french': 'fr',
    'german': 'de',
    'italian': 'it',
    'portuguese': 'pt',
    'russian': 'ru',
    'arabic': 'ar',
    'chinese': 'zh',
    'japanese': 'ja',
    'korean': 'ko',
    'hindi': 'hi',
    'thai': 'th',
    'vietnamese': 'vi',
    'dutch': 'nl',
    'polish': 'pl',
    'croatian': 'hr',
    'czech': 'cs',
    'danish': 'da',
    'norwegian': 'no',
    'swedish': 'sv',
    'finnish': 'fi',
    'greek': 'el',
    'romanian': 'ro',
    'estonian': 'et',
  };
  
  // Extract first word (language name) from label
  const firstWord = label.toLowerCase().split(/[\s(]/)[0].trim();
  
  // Check if we have a mapping
  if (langMap[firstWord]) {
    return langMap[firstWord];
  }
  
  // Fallback: use first 2 characters or common patterns
  if (firstWord.includes('english')) return 'en';
  if (firstWord.includes('spanish')) return 'es';
  if (firstWord.includes('french')) return 'fr';
  
  // Default fallback
  return firstWord.substring(0, 2) || 'en';
}

